/**
 * The kiosk's date format, both directions.
 *
 * **The model is always ISO `YYYY-MM-DD`; only the SCREEN is `MM/DD/YYYY`** [Operator, 2026-09-07]. Bullhorn's
 * `dateAvailable` takes ISO and silently drops anything else, so the stored shape is not negotiable — what
 * was negotiable is what the candidate reads, and a US kiosk shows a US date.
 *
 * This exists because `<input type="date">` cannot be made to display a chosen order: Chrome formats it
 * from its own locale and ignores `lang`, Firefox follows the OS regional setting, and there is no CSS or
 * attribute for it. That is a real limitation of that ONE control, and the answer is simply not to use it —
 * a masked text input renders whatever it is told to.
 */

/** `2026-09-15` → `09/15/2026`. Anything that is not an ISO date comes back unchanged. */
export function isoToUs(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (match === null) return iso;
  return `${match[2]!}/${match[3]!}/${match[1]!}`;
}

/**
 * `09/15/2026` → `2026-09-15`, and `''` for anything that is not a REAL date.
 *
 * **Real, not merely well-shaped.** `02/31/2026` matches the pattern and is not a day, and `13/01/2026` is
 * a European date typed by habit into a US field. Both are rejected here rather than passed on: a date the
 * kiosk cannot parse must not travel as if it were fine, because Bullhorn accepts a malformed value without
 * complaining and the candidate would find out on their start day.
 */
export function usToIso(us: string): string {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(us.trim());
  if (match === null) return '';

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const iso = `${match[3]!}-${match[1]!}-${match[2]!}`;

  // Round-tripping through `Date` is what catches the 31st of February: the constructor rolls it over to
  // March 3rd, so a date that survives the comparison is one that exists. UTC on purpose — this is a
  // calendar check, and a local-midnight `Date` shifts the day in half the world's timezones.
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  if (parsed.getUTCFullYear() !== year) return '';
  if (parsed.getUTCMonth() + 1 !== month) return '';
  if (parsed.getUTCDate() !== day) return '';

  return iso;
}

/**
 * What the candidate sees while they are still typing.
 *
 * Digits only, slashes inserted for them, and never more than eight digits — so the field cannot be typed
 * into an invalid shape in the first place. Deleting works because the slashes are re-derived on every
 * keystroke rather than being characters the caret has to step over.
 */
export function maskUsDate(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
